import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Upload, Trash2, Pencil, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, Field, useList } from "@/components/admin/kit";
import { useProduct } from "@/threedvista/product";

const CHUNK = 5 * 1024 * 1024;

function Uploader({ tour, onDone, label = "Upload zipped 3DVista folder" }) {
  const [pct, setPct] = useState(null);
  const send = async (file) => {
    if (!file) return;
    const total = Math.max(1, Math.ceil(file.size / CHUNK));
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("index", i); fd.append("total", total); fd.append("size", file.size); fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), "part.zip");
        for (let attempt = 0; ; attempt++) {
          try { await api.post(`/admin/tdv/tours/${tour.id}/chunk`, fd, { timeout: 120000 }); break; }
          catch (e) { if (attempt >= 4 || (e.response && e.response.status < 500)) throw e; await new Promise((r) => setTimeout(r, 2000 * (attempt + 1))); }
        }
        setPct(Math.round(((i + 1) / total) * 100));
      }
      onDone();
    } catch (e) { toast.error(errMsg(e)); setPct(null); }
  };
  return (
    <div className="mt-4" data-testid={`tdv-uploader-${tour.id}`}>
      {pct === null ? (
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-bp text-white text-sm font-semibold cursor-pointer hover:opacity-90">
          <Upload className="w-4 h-4" /> {label}
          <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => send(e.target.files[0])} data-testid={`tdv-upload-input-${tour.id}`} />
        </label>
      ) : <div><p className="text-xs text-slate-500 mb-1">Uploading {pct}%</p><Progress value={pct} /></div>}
      {tour.error && <p className="text-sm text-rose-600 mt-2" data-testid={`tdv-upload-error-${tour.id}`}>{tour.error}</p>}
    </div>
  );
}

function TourCard({ t, reload }) {
  const P = useProduct();
  const g = t.source === "giraffe";
  const del = async () => { if (window.confirm(`Delete ${t.name}? Deleting doesn't give the ${P.unit} back to your plan.${g ? "" : " To update this tour, upload the republished project to it instead."}`)) { await api.delete(`/admin/tdv/tours/${t.id}`); reload(); } };
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid={`tdv-tour-${t.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-lg">{t.name}</p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{g ? t.giraffe_url : `Uploaded folder · ${(t.hotspots || []).length} hotspots`}</p>
        </div>
        <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-1 ${t.status === "ready" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`tdv-status-${t.id}`}>
          {t.status === "processing" ? `Unpacking ${t.done || 0}/${t.files || "…"}` : t.status === "ready" ? "Ready" : "Needs upload"}
        </span>
      </div>
      {t.status === "waiting" && <Uploader tour={t} onDone={reload} />}
      {t.status === "ready" && t.syncing && (
        <div className="mt-4" data-testid={`tdv-sync-${t.id}`}>
          <p className="text-xs text-slate-500 mb-1" data-testid={`tdv-sync-text-${t.id}`}>Saving a backup{t.bundle_parts ? ` (${Math.round(((t.bundle_done || 0) / t.bundle_parts) * 100)}%)` : "…"}. Your tour already works.</p>
          <Progress value={t.bundle_parts ? ((t.bundle_done || 0) / t.bundle_parts) * 100 : 5} />
        </div>
      )}
      {t.status === "ready" && !t.syncing && t.sync_failed > 0 && <><p className="text-xs text-amber-700 mt-3" data-testid={`tdv-sync-failed-${t.id}`}>The backup couldn't be saved. Upload the zip again to fix it.</p><Uploader tour={t} onDone={reload} /></>}
      {!g && t.status === "ready" && !t.syncing && !(t.sync_failed > 0) && <Uploader tour={t} onDone={reload} label="Update with republished build" />}
      <div className="flex flex-wrap gap-2 mt-4">
        {t.status === "ready" && <Link to={`${P.base}/app/tours/${t.id}`}><AdminButton data-testid={`tdv-edit-${t.id}`}><Pencil className="w-4 h-4" /> {g ? "Look & features" : "Scenes & hotspots"}</AdminButton></Link>}
        {t.status === "ready" && <a href={`/3d/${t.id}`} target="_blank" rel="noreferrer"><AdminButton variant="outline" data-testid={`tdv-open-${t.id}`}><ExternalLink className="w-4 h-4" /> Open tour</AdminButton></a>}
        <AdminButton variant="danger" onClick={del} data-testid={`tdv-delete-${t.id}`}><Trash2 className="w-4 h-4" /> Delete</AdminButton>
      </div>
    </div>
  );
}

export default function AdminTdvList() {
  const [list, reload] = useList("/admin/tdv/tours");
  const P = useProduct();
  const g = P.key === "giraffe";
  const [f, setF] = useState({ name: "", url: "" });
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    clearInterval(timer.current);
    if ((list || []).some((t) => t.status === "processing" || t.syncing)) timer.current = setInterval(reload, 4000);
    return () => clearInterval(timer.current);
  }, [list, reload]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/tdv/tours", f); setF({ name: "", url: "" }); reload(); toast.success(g ? "Tour added. Open it to style it." : "Tour added. Now upload its zipped folder."); }
    catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <div data-testid="admin-tdv-page">
      <PageTitle title="Your tours" sub={g ? "Paste the link of a Giraffe360 tour. We show it inside your skin with your menu, AI assistant, info windows and lead forms." : "In 3DVista, publish your project, zip the folder it creates and upload it here. Then add hotspots, scene names and your opening."} />
      <form onSubmit={add} className={`rounded-3xl bg-white border p-6 grid ${g ? "md:grid-cols-[1fr_1.4fr_auto]" : "md:grid-cols-[1fr_auto]"} gap-3 items-end mb-8`} data-testid="tdv-create-form">
        <Field label="Tour name"><Input required maxLength={120} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Lakeview show suite" data-testid="tdv-create-name" /></Field>
        {g && <Field label="Giraffe360 tour link"><Input required type="url" maxLength={500} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://tour.giraffe360.com/…" data-testid="tdv-create-url" /></Field>}
        <AdminButton disabled={busy} data-testid="tdv-create-submit"><Plus className="w-4 h-4" /> Add tour</AdminButton>
      </form>
      <div className="grid lg:grid-cols-2 gap-4">
        {(list || []).map((t) => <TourCard key={t.id} t={t} reload={reload} />)}
        {list?.length === 0 && <p className="text-sm text-slate-500" data-testid="tdv-empty">No {P.name} tours yet.</p>}
      </div>
    </div>
  );
}
